import React from "react"

import { Bike } from "models"
import * as BikeApi from "features/bike/api/BikeApi"
import { dateService } from "services/date"

export enum BikesStoreActionType {
	FETCH = "FETCH",
	RESET_STATE = "RESET_STATE",
}

interface BikesStoreAction {
	type: BikesStoreActionType
	payload?: any
}

interface BikeStore {
	bikes: Bike[]
	bikesWithActionNeeded: Bike[]
	bikesWithNoActionNeeded: Bike[]
	isBikesFetch: boolean
}

type BikesStoreContextInterface = {
	bikesStore: BikeStore
	fetchBikes: () => void
	resetBikes: () => void
}

const BikesStoreContext = React.createContext<BikesStoreContextInterface | undefined>(undefined)

const BikesStoreReducer = (bikesStore: BikeStore, action: BikesStoreAction) => {
	const { type, payload } = action
	switch (type) {
		case "FETCH":
		case "RESET_STATE": {
			return {
				...bikesStore,
				...payload,
			}
		}
		default:
			return bikesStore
	}
}

export const defaultBikesState: BikeStore = {
	bikes: null,
	bikesWithActionNeeded: null,
	bikesWithNoActionNeeded: null,
	isBikesFetch: false,
}

/**********************
 ** Context provider **
 **********************/

// prettier-ignore
const BikesStoreProvider = ({ children }: { children: React.ReactNode }) => {
	const [bikesStore, bikesStoreDispatch] = React.useReducer(BikesStoreReducer, defaultBikesState)

    const fetchBikes = async () => {
		const { res: fetchedBikes, getError } = await BikeApi.getBikes()
		if (getError) throw getError()

		const bikesWithActionNeeded = fetchedBikes.filter((bike) => bike.bike_action_needed)
		const bikesWithNoActionNeeded = fetchedBikes
			.filter((bike) => !bike.bike_action_needed)
			.sort(
				(a, b) =>
					dateService.getTimestamp(a.create_date) -
					dateService.getTimestamp(b.create_date)
			)

		const isBikesFetch = true

        bikesStoreDispatch({
			type: BikesStoreActionType.FETCH,
			payload: {
				bikes: fetchedBikes,
				bikesWithActionNeeded,
				bikesWithNoActionNeeded,
				isBikesFetch,
			},
		})
	}

	const resetBikes = () => {
		bikesStoreDispatch({
			type: BikesStoreActionType.RESET_STATE,
			payload: defaultBikesState,
		})
	}

	return (
		<BikesStoreContext.Provider value={{ bikesStore, fetchBikes, resetBikes }}>
			{children}
		</BikesStoreContext.Provider>
	)
}

/**********************
 ** Context consumer **
 **********************/
// The context consumer uses the render props pattern
// to directly provide a consumer component with the value to consume

type ChildrenRenderProps = {
	children: ({ bikesStore, fetchBikes }: BikesStoreContextInterface) => React.ReactChild
}

// prettier-ignore
const BikesStoreConsumer = ({ children }: ChildrenRenderProps) => (
	<BikesStoreContext.Consumer>
		{({ bikesStore, fetchBikes, resetBikes }) =>
			children({ bikesStore, fetchBikes, resetBikes })
		}
	</BikesStoreContext.Consumer>
)

/**********************
 ** Context hook **
 **********************/

const useBikesStore = () => {
	const context = React.useContext(BikesStoreContext)
	if (context === undefined) {
		throw new Error("useBikesStore must be used within a BikesStoreProvider")
	}
	return context
}

export { useBikesStore, BikesStoreProvider, BikesStoreConsumer }
